/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { XHubChatEventEmittedEvents, XHubChatEventHandlerMap } from '../../@types/emitMapper';
import type { DecryptionFailureCode, EventStatus, IClearEvent, IContent, IEvent, IEventRelation, IThreadBundledRelationship, IUnsigned, IVisibilityChange, MessageVisibility } from '../../@types/event';
import type { XHubChatError } from '../../http';
import type { Room } from '../room/room';
import type { RoomMember } from '../room/room-member';
import type { RoomState } from '../room/room-state';
import type { Thread } from '../thread/thread';
import { EVENT_VISIBILITY_CHANGE_TYPE, EventType, MESSAGE_VISIBLE, REDACT_KEEP_CONTENT_MAP, REDACT_KEEP_KEYS, RelationType, UNSIGNED_THREAD_ID_FIELD, XHubChatEventEvent } from '../../@types/event';
import { TypedReEmitter } from '../../emit/ReEmitter';
import { TypedEventEmitter } from '../../emit/typed-event-emitter';
import { internalizeString } from '../../utils';
import { THREAD_RELATION_TYPE, ThreadEvent } from '../thread/thread';
import { EventTimeline } from './event-timeline';

export class XHubChatEvent extends TypedEventEmitter<XHubChatEventEmittedEvents, XHubChatEventHandlerMap> {
  /**
   * The event after encryption. This is same clean (raw) event
   */
  private clearEvent?: IClearEvent;

  /* Message hiding, as specified by https://github.com/matrix-org/matrix-doc/pull/3531.

    Note: We're returning this object, so any value stored here MUST be frozen.
    */
  private visibility: MessageVisibility = MESSAGE_VISIBLE;

  /**
   * The txnId with which this event was sent if it was during this session,
   * allows for a unique ID which does not change when the event comes back down sync.
   */
  private txnId?: string;
  private _replacingEvent: XHubChatEvent | null = null;

  /**
   * Set an approximate timestamp for the event relative the local clock.
   * This will inherently be approximate because it doesn't take into account
   * the time between the server putting the 'age' field on the event as it sent
   * it to us and the time we're now constructing this event, but that's better
   * than assuming the local clock is in sync with the origin HS's clock.
   */
  public localTimestamp: number;

  /**
   * The time the event was pinned in the room, if any.
   */
  private time_pin: string | null = null;

  private _localRedactionEvent: XHubChatEvent | null = null;

  /**
   * Re emitter for events from this event
   * @remarks
   * This is used to re-emit events from the clearEvent (decrypted event) if it exists.
   * This is useful for listening to events on the XHubChatEvent that are emitted by the clearEvent.
   * @example
   * ```typescript
   * event.reEmitter.on(XHubChatEventEvent.Decrypted, (clearEvent) => {
   *   console.log("Event was decrypted:", clearEvent);
   * });
   * ```
   */
  private readonly reEmitter: TypedReEmitter<XHubChatEventEmittedEvents, XHubChatEventHandlerMap>;

  /**
   * The sending status of the event.
   * @privateRemarks
   * Should be read-only
   */
  public status: EventStatus | null = null;

  /**
   * The room member who sent this event, or null e.g.
   * this is a presence event. This is only guaranteed to be set for events that
   * appear in a timeline, ie. do not guarantee that it will be set on state
   * events.
   * @privateRemarks
   * Should be read-only
   */
  public sender: RoomMember | null = null;

  /**
   * The room member who is the target of this event, e.g.
   * the invitee, the person being banned, etc.
   * @privateRemarks
   * Should be read-only
   */
  public target: RoomMember | null = null;

  /**
   * most recent error associated with sending the event, if any
   * @privateRemarks
   * Should be read-only. May not be a XHubChatError.
   */
  public error: XHubChatError | null = null;

  /**
   * True if this event is 'forward looking', meaning
   * that getDirectionalContent() will return event.content and not event.prev_content.
   * Only state events may be backwards looking
   * Default: true. <strong>This property is experimental and may change.</strong>
   * @privateRemarks
   * Should be read-only
   */
  public forwardLooking = true;

  /**
   * A reference to the thread this event belongs to
   */
  private thread?: Thread;
  private threadId?: string;

  /** If we failed to decrypt this event, the reason for the failure. Otherwise, `null`. */
  private _decryptionFailureReason: DecryptionFailureCode | null = null;

  /**
   * Construct a Matrix Event object
   *
   * @param event - The raw (possibly encrypted) event. <b>Do not access
   * this property</b> directly unless you absolutely have to. Prefer the getter
   * methods defined on this class. Using the getter methods shields your app
   * from changes to event JSON between Matrix versions.
   */
  public constructor(public event: Partial<IEvent> = {}) {
    super();

    // intern the values of matrix events to force share strings and reduce the
    // amount of needless string duplication. This can save moderate amounts of
    // memory (~10% on a 350MB heap).
    // 'membership' at the event level (rather than the content level) is a legacy
    // field that Element never otherwise looks at, but it will still take up a lot
    // of space if we don't intern it.
    (['state_key', 'type', 'sender_id', 'room_id', 'membership'] as const).forEach((prop) => {
      if (typeof event[prop] !== 'string') return;
      event[prop] = internalizeString(event[prop]!);
    });

    (['membership', 'avatar_url', 'displayname'] as const).forEach((prop) => {
      if (typeof event.content?.[prop] !== 'string') return;
      event.content[prop] = internalizeString(event.content[prop]!);
    });

    (['rel_type'] as const).forEach((prop) => {
      if (typeof event.content?.['m.relates_to']?.[prop] !== 'string') return;
      event.content['m.relates_to'][prop] = internalizeString(event.content['m.relates_to'][prop]!);
    });

    this.txnId = event.txn_id;
    // The localTimestamp is calculated using the age.
    // Some events lack an `age` property, either because they are EDUs such as typing events,
    // or due to server-side bugs such as https://github.com/matrix-org/synapse/issues/8429.
    // The fallback in these cases will be to use the origin_server_ts.
    // For EDUs, the origin_server_ts also is not defined so we use Date.now().
    const age = this.getAge();

    this.localTimestamp = age !== undefined ? Date.now() - age : (this.getTs() ?? Date.now());
    this.time_pin = event.time_pin ?? null;
    this.reEmitter = new TypedReEmitter(this);
  }

  /**
   * Get the age of this event. This represents the age of the event when the
   * event arrived at the device, and not the age of the event when this
   * function was called.
   * Can only be returned once the server has echo'ed back
   * @returns The age of this event in milliseconds.
   */
  public getAge(): number | undefined {
    return this.getUnsigned().age;
  }

  /**
   * Check whether this event is pinned in the room.
   */
  public get isPinned(): boolean {
    return !!this.time_pin && !!this.event.time_pin;
  }

  /**
   * Check whether this event is a content message.
   * In Post room, the messages in room will have a specific message, this is content of post.
   */
  public get isContentMessage(): boolean {
    return !!this.getId()?.includes('content');
  }

  /**
   * Get the time_pin of this event.
   */
  public get timePin(): string | null {
    return this.time_pin || this.event.time_pin || null;
  }

  /**
   * Get the unsigned properties of this event.
   * @returns The unsigned properties of this event.
   */
  public getUnsigned(): IUnsigned {
    return this.event.unsigned || {};
  }

  /**
   * Update the pin time of this event.
   * @param time_pin - The new pin time.
   */
  public updatePinTime(time_pin: string | null): void {
    this.time_pin = time_pin;
  }

  /**
   * Get the timestamp of this event.
   * @returns The event timestamp, e.g. `1433502692297`
   */
  public getTs(): number {
    return new Date(this.event.timestamp!).getTime();
  }

  /**
   * Get the id for this event.
   * @returns The event ID, e.g. `$143350589368169JsLZx:localhost`
   */
  public getId(): string | undefined {
    return this.event.id;
  }

  /**
   * Get the user_id for this event.
   * @returns The user ID, e.g. `@alice:matrix.org`
   */
  public getSender(): string | undefined {
    return this.event.sender_id;
  }

  /**
   * Get the (decrypted, if necessary) type of event.
   *
   * @returns The event type, e.g. `m.room.message`
   */
  public getType(): EventType | string {
    if (this.clearEvent) {
      return this.clearEvent.type;
    }
    return this.event.type!;
  }

  public isReaction(): boolean {
    const relation = this.getRelation();
    return (
      this.getType() === EventType.Reaction
      && relation?.rel_type === RelationType.Annotation
    );
  }

  /**
   * Get the event this event is replying to, if any.
   */
  public get replyToEvent(): XHubChatEvent | null {
    return this.thread?.findEventById(this.replyEventId!) || null;
  }

  /**
   * Get the timestamp of this event, as a Date object.
   * @returns The event date, e.g. `new Date(1433502692297)`
   */
  public getDate(): Date | null {
    return this.event.timestamp ? new Date(this.event.timestamp) : null;
  }

  /**
   * Check if this event is a state event.
   * @returns True if this is a state event.
   */
  public isState(): boolean {
    return this.event.state_key !== undefined;
  }

  /**
   * Get the (possibly encrypted) event content JSON that will be sent to the server.
   * @returns The event content JSON, or an empty object.
   */
  public getWireContent(): IContent {
    return this.event.content || {};
  }

  /**
   * Get the previous event content JSON. This will only return something for
   * state events which exist in the timeline.
   * @returns The previous event content JSON, or an empty object.
   */
  public getPrevContent(): IContent {
    // v2 then v1 then default
    return this.getUnsigned().prev_content || {};
  }

  /**
   * Get either 'content' or 'prev_content' depending on if this event is
   * 'forward-looking' or not. This can be modified via event.forwardLooking.
   * In practice, this means we get the chronologically earlier content value
   * for this event (this method should surely be called getEarlierContent)
   * <strong>This method is experimental and may change.</strong>
   * @returns event.content if this event is forward-looking, else
   * event.prev_content.
   */
  public getDirectionalContent(): IContent {
    return this.forwardLooking ? this.getContent() : this.getPrevContent();
  }

  /**
   * Get whether the event is a relation event, and of a given type if
   * `relType` is passed in. State events cannot be relation events
   *
   * @param relType - if given, checks that the relation is of the
   * given type
   */
  public isRelation(relType?: string): boolean {
    // Relation info is lifted out of the encrypted content when sent to
    // encrypted rooms, so we have to check `getWireContent` for this.
    const relation = this.getWireContent()?.['m.relates_to'];
    if (this.isState() && !!relation?.rel_type) {
      // State events cannot be m.replace or m.thread relations
      return false;
    }
    return !!(relation?.rel_type && relation.event_id && (relType ? relation.rel_type === relType : true));
  }

  public get relationEventId(): string | undefined {
    return this.getWireContent()?.['m.relates_to']?.event_id;
  }

  /**
   * Get the event state_key if it has one. If necessary, this will perform
   * string-unpacking on the state key, as per MSC3414. This will return
   * `undefined` for message events.
   * @returns The event's `state_key`.
   */
  public getStateKey(): string | undefined {
    if (this.clearEvent) {
      return this.clearEvent.state_key;
    }
    return this.event.state_key;
  }

  /**
   * Get relation info for the event, if any.
   */
  public getRelation(): IEventRelation | null {
    if (!this.isRelation()) {
      return null;
    }
    return this.getWireContent()['m.relates_to'] ?? null;
  }

  public get replyEventId(): string | undefined {
    return this.getWireContent()['m.relates_to']?.['m.in_reply_to']?.event_id;
  }

  /**
   * Returns the status of any associated edit or redaction
   * (not for reactions/annotations as their local echo doesn't affect the original event),
   * or else the status of the event.
   */
  public getAssociatedStatus(): EventStatus | null {
    return this.status;
  }

  /**
   * Get the event ID of the thread head
   */
  public get threadRootId(): string | undefined {
    // don't allow state events to be threaded as per the spec
    if (this.isState()) {
      return undefined;
    }
    const relatesTo = this.getWireContent()?.['m.relates_to'];
    if (relatesTo?.rel_type === THREAD_RELATION_TYPE.name) {
      return relatesTo.event_id;
    }
    if (this.thread) {
      return this.thread.id;
    }
    if (this.threadId !== undefined) {
      return this.threadId;
    }
    const unsigned = this.getUnsigned();
    if (typeof unsigned[UNSIGNED_THREAD_ID_FIELD.altName] === 'string') {
      return unsigned[UNSIGNED_THREAD_ID_FIELD.altName];
    }
    return undefined;
  }

  /**
   * Get the event ID for this event.
   */
  public get id(): string | undefined {
    return this.event.id;
  }

  /**
   * Set the instance of a thread associated with the current event
   * @param thread - the thread
   */
  public setThread(thread?: Thread): void {
    // don't allow state events to be threaded as per the spec
    if (this.isState()) {
      return;
    }
    if (this.thread) {
      this.reEmitter.stopReEmitting(this.thread, [ThreadEvent.Update]);
    }
    this.thread = thread;
    this.setThreadId(thread?.id);
    if (thread) {
      this.reEmitter.reEmit(thread, [ThreadEvent.Update]);
    }
  }

  public setThreadId(threadId?: string): void {
    this.threadId = threadId;
  }

  /**
   * A helper to check if an event is a thread's head or not
   */
  public get isThreadRoot(): boolean {
    // don't allow state events to be threaded as per the spec
    if (this.isState()) {
      return false;
    }

    const threadDetails = this.getServerAggregatedRelation<IThreadBundledRelationship>(THREAD_RELATION_TYPE.name);

    // Bundled relationships only returned when the sync response is limited
    // hence us having to check both bundled relation and inspect the thread
    // model
    return !!threadDetails || this.threadRootId === this.getId();
  }

  /**
   * Check if the event is encrypted.
   * @returns True if this event is encrypted.
   */
  public isEncrypted(): boolean {
    return this.event.type === EventType.RoomMessageEncrypted;
  }

  /**
   * Check if this event is an encrypted event which we failed to decrypt
   *
   * (This implies that we might retry decryption at some point in the future)
   *
   * @returns True if this event is an encrypted event which we
   *     couldn't decrypt.
   */
  public isDecryptionFailure(): boolean {
    return this._decryptionFailureReason !== null;
  }

  /**
   * For relations returns the event_id this event is referring to.
   */
  public getAssociatedId(): string | undefined {
    const relation = this.getRelation();
    if (this.replyEventId) {
      return this.replyEventId;
    } else if (relation) {
      return relation.event_id;
    } else if (this.isRedaction()) {
      return this.event.redacts;
    }
  }

  /**
   * Get the instance of the thread associated with the current event
   */
  public getThread(): Thread | undefined {
    return this.thread;
  }

  /**
   * Get the thread ID associated with the current event
   * @returns The thread ID, or undefined if not set.
   */
  public getThreadId(): string | undefined {
    return this.threadId;
  }

  public getServerAggregatedRelation<T>(relType: RelationType | string): T | undefined {
    return this.getUnsigned()['m.relations']?.[relType];
  }

  /**
   * Check if this event has been redacted
   *
   * @returns True if this event has been redacted
   */
  public isRedacted(): boolean {
    return Boolean(this.getUnsigned().redacted_because);
  }

  public markLocallyRedacted(redactionEvent: XHubChatEvent): void {
    if (this._localRedactionEvent) return;
    this.emit(XHubChatEventEvent.BeforeRedaction, this, redactionEvent);
    this._localRedactionEvent = redactionEvent;
    if (!this.event.unsigned) {
      this.event.unsigned = {};
    }
    this.event.unsigned.redacted_because = redactionEvent.event as IEvent;
  }

  /**
   * Check if this event is a redaction of another event
   *
   * @returns True if this event is a redaction
   */
  public isRedaction(): boolean {
    return this.getType() === EventType.RoomRedaction;
  }

  /**
   * Check whether the event is a message event.
   * @returns True if this is a message event. `m.room.message`
   */
  public isMessage(): boolean {
    return this.getType() === EventType.RoomMessage;
  }

  /**
   * Whether the event is in any phase of sending, send failure, waiting for
   * remote echo, etc.
   */
  public isSending(): boolean {
    return !!this.status;
  }

  /**
   * Get the room_id for this event. This will return `undefined`
   * for `m.presence` events.
   * @returns The room ID, e.g. `ca5190ed-da0a-4537-8884-f1ad93677667`
   */
  public getRoomId(): string | undefined {
    return this.event.room_id;
  }

  public updateId(newId: string): void {
    this.event.id = newId;
    this.emit(XHubChatEventEvent.LocalEventIdReplaced, this);
  }

  /**
   * Get the (decrypted, if necessary) event content JSON, even if the event
   * was replaced by another event.
   *
   * @returns The event content JSON, or an empty object.
   */
  public getOriginalContent<T = IContent>(): T {
    if (this.clearEvent) {
      return (this.clearEvent.content ?? {}) as T;
    }
    return (this.event.content ?? {}) as T;
  }

  /**
   * Get the (decrypted, if necessary) event content JSON,
   * or the content from the replacing event, if any.
   * See `makeReplaced`.
   *
   * @returns The event content JSON, or an empty object.
   */
  public getContent<T extends IContent = IContent>(): T {
    if (this._localRedactionEvent) {
      return {} as T;
    } else {
      return this.getOriginalContent();
    }
  }

  public getTxnId(): string | undefined {
    return this.txnId;
  }

  // =============== SETTERS ===============
  /**
   * Set the unsigned properties of this event.
   * @param unsigned - The new unsigned properties.
   */
  public setUnsigned(unsigned: IUnsigned): void {
    this.event.unsigned = unsigned;
  }

  /**
   * Set the TxnId (key) for this event.
   * @param txnId - The new transaction ID.
   */
  public setTxnId(txnId: string): void {
    this.txnId = txnId;
  }

  /**
   * Update the event's sending status and emit an event as well.
   *
   * @param status - The new status
   */
  public setStatus(status: EventStatus | null): void {
    this.status = status;
    this.emit(XHubChatEventEvent.Status, this, status);
  }

  /**
   * Update the sentinels and forwardLooking flag for this event.
   * @param stateContext -  the room state to be queried
   * @param toStartOfTimeline -  if true the event's forwardLooking flag is set false
   * @internal
   */
  public setMetadata(stateContext: RoomState, toStartOfTimeline: boolean): void {
    // If an event is an m.room.member state event then we should set the sentinels again in case setEventMetadata
    // was already called before the state was applied and thus the sentinel points at the member from before this event.
    const affectsSelf
      = this.isState() && this.getType() === EventType.RoomMember && this.getSender() === this.getStateKey();

    let changed = false;
    // When we try to generate a sentinel member before we have that member
    // in the members object, we still generate a sentinel but it doesn't
    // have a membership event, so test to see if events.member is set. We
    // check this to avoid overriding non-sentinel members by sentinel ones
    // when adding the event to a filtered timeline
    if (affectsSelf || !this.sender?.events?.member) {
      const newSender = stateContext.getSentinelMember(this.getSender()!);
      if (newSender !== this.sender) changed = true;
      this.sender = newSender;
    }
    if (affectsSelf || (!this.target?.events?.member && this.getType() === EventType.RoomMember)) {
      const newTarget = stateContext.getSentinelMember(this.getStateKey()!);
      if (newTarget !== this.target) changed = true;
      this.target = newTarget;
    }

    if (this.isState()) {
      // room state has no concept of 'old' or 'current', but we want the
      // room state to regress back to previous values if toStartOfTimeline
      // is set, which means inspecting prev_content if it exists. This
      // is done by toggling the forwardLooking flag.
      if (toStartOfTimeline) {
        this.forwardLooking = false;
      }
    }

    if (changed) {
      this.emit(XHubChatEventEvent.SentinelUpdated);
    }
  }

  /**
   * Replace local event with the server-assigned event ID.
   * @param eventId - The new event ID.
   */
  public replaceLocalEventId(eventId: string): void {
    this.event.id = eventId;
    this.emit(XHubChatEventEvent.LocalEventIdReplaced, this);
  }

  /**
   * Replace the `event` property and recalculate any properties based on it.
   * @param event - the object to assign to the `event` property
   */
  public handleRemoteEcho(event: object): void {
    const oldId = this.getId();
    this.event = event;

    // successfully sent.
    this.setStatus(null);
    if (this.getId() !== oldId) {
      // emit the event if it changed
      this.emit(XHubChatEventEvent.LocalEventIdReplaced, this);
    }

    this.localTimestamp = new Date((event as IEvent).created_at).getTime();
  }

  /**
   * Update the related id with a new one.
   *
   * Used to replace a local id with remote one before sending
   * an event with a related id.
   *
   * @param eventId - the new event id
   */
  public updateAssociatedId(eventId: string): void {
    const relation = this.getRelation();
    if (relation) {
      relation.event_id = eventId;
    } else if (this.isRedaction()) {
      this.event.redacts = eventId;
    }
  }

  /**
   * Update the content of an event in the same way it would be by the server
   * if it were redacted before it was sent to us
   *
   * @param redactionEvent - event causing the redaction
   * @param room - the room in which the event exists
   */
  public makeRedacted(redactionEvent: XHubChatEvent, room: Room): void {
    // quick sanity-check
    if (!redactionEvent.event) {
      throw new Error('invalid redactionEvent in makeRedacted');
    }

    this._localRedactionEvent = null;

    this.emit(XHubChatEventEvent.BeforeRedaction, this, redactionEvent);

    this._replacingEvent = null;
    // we attempt to replicate what we would see from the server if
    // the event had been redacted before we saw it.
    //
    // The server removes (most of) the content of the event, and adds a
    // "redacted_because" key to the unsigned section containing the
    // redacted event.
    if (!this.event.unsigned) {
      this.event.unsigned = {};
    }
    this.event.unsigned.redacted_because = redactionEvent.event as IEvent;

    for (const key in this.event) {
      if (Object.prototype.hasOwnProperty.call(this.event, key) && !REDACT_KEEP_KEYS.has(key)) {
        delete this.event[key as keyof IEvent];
      }
    }

    // If the event is encrypted prune the decrypted bits
    if (this.isEncrypted()) {
      this.clearEvent = undefined;
    }

    const keeps
      = this.getType() in REDACT_KEEP_CONTENT_MAP
        ? REDACT_KEEP_CONTENT_MAP[this.getType() as keyof typeof REDACT_KEEP_CONTENT_MAP]
        : {};
    const content = this.getContent();
    for (const key in content) {
      if (Object.prototype.hasOwnProperty.call(content, key) && !keeps[key]) {
        delete content[key];
      }
    }

    // If the redacted event was in a thread (but not thread root), move it
    // to the main timeline. This will change if MSC3389 is merged.
    if (!this.isThreadRoot && this.threadRootId && this.threadRootId !== this.getId()) {
      this.moveAllRelatedToMainTimeline(room);
      redactionEvent.moveToMainTimeline(room);
    }
  }

  /**
   * Check if this event alters the visibility of another event,
   * as per https://github.com/matrix-org/matrix-doc/pull/3531.
   *
   * @returns True if this event alters the visibility
   * of another event.
   */
  public isVisibilityEvent(): boolean {
    return EVENT_VISIBILITY_CHANGE_TYPE.matches(this.getType());
  }

  private moveAllRelatedToMainTimeline(room: Room): void {
    const thread = this.thread;
    this.moveToMainTimeline(room);

    // If we dont have access to the thread, we can only move this
    // event, not things related to it.
    if (thread) {
      for (const event of thread.events) {
        if (event.getRelation()?.event_id === this.getId()) {
          event.moveAllRelatedToMainTimeline(room);
        }
      }
    }
  }

  private moveToMainTimeline(room: Room): void {
    // Remove it from its thread
    this.thread?.timelineSet.removeEvent(this.getId()!);
    this.setThread(undefined);

    // And insert it into the main timeline
    const timeline = room.getLiveTimeline();
    // We use insertEventIntoTimeline to insert it in timestamp order,
    // because we don't know where it should go (until we have MSC4033).
    timeline
      .getTimelineSet()
      .insertEventIntoTimeline(this, timeline, timeline.getState(EventTimeline.FORWARDS)!, false);
  }

  /**
   * Change the visibility of an event, as per https://github.com/matrix-org/matrix-doc/pull/3531 .
   *
   * @param visibilityChange - event holding a hide/unhide payload, or nothing
   *   if the event is being reset to its original visibility (presumably
   *   by a visibility event being redacted).
   *
   * @remarks
   * Fires {@link MatrixEventEvent.VisibilityChange} if `visibilityEvent`
   *   caused a change in the actual visibility of this event, either by making it
   *   visible (if it was hidden), by making it hidden (if it was visible) or by
   *   changing the reason (if it was hidden).
   */
  public applyVisibilityEvent(visibilityChange?: IVisibilityChange): void {
    const visible = visibilityChange?.visible ?? true;
    const reason = visibilityChange?.reason ?? null;
    let change = false;
    if (this.visibility.visible !== visible) {
      change = true;
    } else if (!this.visibility.visible && this.visibility.reason !== reason) {
      change = true;
    }
    if (change) {
      if (visible) {
        this.visibility = MESSAGE_VISIBLE;
      } else {
        this.visibility = Object.freeze({
          visible: false,
          reason,
        });
      }
      this.emit(XHubChatEventEvent.VisibilityChange, this, visible);
    }
  }

  /**
   * Return the visibility change caused by this event,
   * as per https://github.com/matrix-org/matrix-doc/pull/3531.
   *
   * @returns If the event is a well-formed visibility change event,
   * an instance of `IVisibilityChange`, otherwise `null`.
   */
  public asVisibilityChange(): IVisibilityChange | null {
    if (!EVENT_VISIBILITY_CHANGE_TYPE.matches(this.getType())) {
      // Not a visibility change event.
      return null;
    }
    const relation = this.getRelation();
    if (!relation || relation.rel_type !== 'm.reference') {
      // Ill-formed, ignore this event.
      return null;
    }
    const eventId = relation.event_id;
    if (!eventId) {
      // Ill-formed, ignore this event.
      return null;
    }
    const content = this.getWireContent();
    const visible = !!content.visible;
    const reason = content.reason;
    if (reason && typeof reason != 'string') {
      // Ill-formed, ignore this event.
      return null;
    }
    // Well-formed visibility change event.
    return {
      visible,
      reason,
      eventId,
    };
  }

  /**
   * Checks if this event is associated with another event. See `getAssociatedId`.
   */
  public hasAssociation(): boolean {
    return !!this.getAssociatedId();
  }
}
